import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { BoardStateVo, TBoardStateKey, TBoardTab } from 'types/insuranceBilling';

// 后端的日期字段是 UTC 日历日，全部要按 UTC 解析 + UTC 渲染（见 formatDateMDY 注释）
dayjs.extend(utc);

// ---------- 状态徽章的配色 ----------
// 文案（label / hint）用服务端 state 返回的；颜色前端按 state.key 对照原型
// billing.html 的 STATUS / WFS_STATUS 配色，不用服务端的 tone。
export interface StateStyle {
    bg: string;
    color: string;
}

const GRAY: StateStyle = { bg: '#F3F4F6', color: '#6b7280' };
const RED: StateStyle = { bg: '#FEF2F2', color: '#B91C1C' };
const GREEN: StateStyle = { bg: '#F0FDF4', color: '#15803D' };

export const STATE_STYLE: Record<TBoardStateKey, StateStyle> = {
    // 紫色是 Klarity preparation 独一份：Klarity 在处理、provider 无事可做
    preparing: { bg: '#F5F3FF', color: '#6D28D9' },
    ready: GREEN,
    not_ready: RED,
    outside_window: GRAY,
    cancelled: { bg: '#EEF0F3', color: '#4B5563' },
    submitted: GREEN,
    acknowledged: { bg: '#FFFBEB', color: '#B45309' },
    in_review: { bg: '#F5F3FF', color: '#6D28D9' },
    paid: GREEN,
    partially_paid: { bg: '#ECFDF5', color: '#047857' },
    // 原型没有这个状态（payer 核准、金额计入患者自付）：归 Completed，沿用 Partially Paid 的绿
    patient_responsibility: { bg: '#ECFDF5', color: '#047857' },
    denied: RED,
    rejected: RED,
    closed: GRAY,
    // 诊所在自己 EHR 里出过单、Klarity 没出单的就诊（10-21 D5）：灰色、不标红，放 Preparation tab
    practice_billed: GRAY,
    other: GRAY,
};

// 后端可能扩展 key；未知值灰色兜底，不让页面崩
export const getStateStyle = (key?: string | null): StateStyle => (key && STATE_STYLE[key as TBoardStateKey]) || GRAY;

// 行左侧色条：payer 退回的 claim 红色，建不出 claim 的 visit 琥珀色
export const rowStripe = (state?: BoardStateVo | null): 'red' | 'amber' | null => {
    if (state?.key === 'denied' || state?.key === 'rejected') return 'red';
    if (state?.key === 'not_ready') return 'amber';
    return null;
};

// ---------- 分段 tabs（原型 09/25：按 claim 的旅程切分）----------
// 哪些状态归哪个 tab 由服务端决定，前端只传 tab 参数、读 summary.tabs 的计数。
// All 还含只在 All 出现的 closed / other，所以不等于其余五个之和。
export const BOARD_TABS: { key: TBoardTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'preparation', label: 'Klarity preparation' },
    { key: 'clearinghouse', label: 'Submitted to clearinghouse' },
    { key: 'payer', label: 'Submitted to payer' },
    // 10-21 D2：原名 Submitted claims 易与 Submitted to payer 混淆，改为 Completed（payer 已出结果）
    { key: 'completed', label: 'Completed' },
    { key: 'attention', label: 'Needs attention' },
];

// ---------- 展示格式化 ----------
export const formatMoney = (n?: number | null): string => {
    if (n == null) return '—';
    return `$${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

// 表格日期统一 MM/DD/YYYY。
// 用于日历日字段（服务日期等）。后端这些日期是 UTC 日历日，且序列化格式不保证：claims/ERA 列表会回
// 'Tue Sep 29 2026 00:00:00 GMT+0000 (...)' 这种 Date.toString()，不是 'YYYY-MM-DD'。
// 用本地时区渲染的话，UTC 零点在整个西半球都落到前一天 —— 美国 provider 看到的每个
// 日期都会倒退一天。所以一律 UTC 解析 + UTC 渲染，两种格式结果一致。
export const formatDateMDY = (d?: string | null): string => {
    if (!d) return '—';
    const parsed = dayjs.utc(d);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : d;
};

// 时间戳字段（submitted、timeline 的 date）：是一个时刻，按本地时区显示日期。
// 若按 UTC 显示，美国晚间提交的 claim 会显示成第二天
export const formatTimestampMDY = (d?: string | null): string => {
    if (!d) return '—';
    const parsed = dayjs(d);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY') : d;
};

// 后端日期串 → UTC dayjs，供需要自定义格式的地方（ReportDrawer 的区间文案）复用
export const utcOf = (d?: string | null): dayjs.Dayjs => dayjs.utc(d ?? undefined);

// 任意 dayjs（或「现在」）归一到 UTC 日历日。
// 注意不能直接 .utc()：DatePicker 给的是本地零点，UTC+8 下转 UTC 会倒退一天，
// 选 9/29 会 snap 到 9/22 那一周。先取字面日再当成 UTC 日；对已是 UTC 的对象幂等。
export const utcDay = (d?: dayjs.Dayjs): dayjs.Dayjs =>
    d ? dayjs.utc(d.format('YYYY-MM-DD')) : dayjs.utc().startOf('day');

// 任意日期归一到所在周的周一（weekly report 的 weekStart 语义）。
// 后端按 UTC 日历圈周，这里若用本地「现在」算星期几，美西周日傍晚会差整整一周。
export const mondayOf = (d: dayjs.Dayjs): dayjs.Dayjs => {
    const u = utcDay(d);
    const day = u.day();
    return day === 0 ? u.subtract(6, 'day') : u.subtract(day - 1, 'day');
};
